import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { mockGrowthRecords } from '@/mocks/userData';

interface GrowthRecord {
  id: string;
  date: string;
  photos: string[];
  note: string;
  habitIds: string[];
}

function formatDate(dateStr: string) {
  const d = new Date(dateStr);
  const months = d.getMonth() + 1;
  const days = d.getDate();
  const weekDays = ['日', '一', '二', '三', '四', '五', '六'];
  return `${months}月${days}日 周${weekDays[d.getDay()]}`;
}

function getDateList() {
  const dates: string[] = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date('2026-04-19');
    d.setDate(d.getDate() - i);
    dates.push(d.toISOString().split('T')[0]);
  }
  return dates;
}

export default function GrowthPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [currentDate, setCurrentDate] = useState('2026-04-19');
  const [records, setRecords] = useState<GrowthRecord[]>(mockGrowthRecords);
  const [note, setNote] = useState('');
  const [showMenu, setShowMenu] = useState(false);
  const [previewPhoto, setPreviewPhoto] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const dateList = getDateList();
  const currentRecord = records.find(r => r.date === currentDate);
  const photos = currentRecord?.photos ?? [];

  const handlePrevDate = () => {
    const idx = dateList.indexOf(currentDate);
    if (idx < dateList.length - 1) setCurrentDate(dateList[idx + 1]);
  };
  const handleNextDate = () => {
    const idx = dateList.indexOf(currentDate);
    if (idx > 0) setCurrentDate(dateList[idx - 1]);
  };

  const handleAddPhoto = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;
    const newUrls = files.map(f => URL.createObjectURL(f));
    setRecords(prev => {
      const existing = prev.find(r => r.date === currentDate);
      if (existing) {
        return prev.map(r => r.date === currentDate ? { ...r, photos: [...r.photos, ...newUrls] } : r);
      }
      return [...prev, { id: Date.now().toString(), date: currentDate, photos: newUrls, note: '', habitIds: [] }];
    });
    e.target.value = '';
  };

  const handleDeletePhoto = (photoUrl: string) => {
    setRecords(prev => prev.map(r => r.date === currentDate ? { ...r, photos: r.photos.filter(p => p !== photoUrl) } : r));
  };

  const handleSave = () => {
    setRecords(prev => {
      const existing = prev.find(r => r.date === currentDate);
      if (existing) {
        return prev.map(r => r.date === currentDate ? { ...r, note } : r);
      }
      return [...prev, { id: Date.now().toString(), date: currentDate, photos: [], note, habitIds: [] }];
    });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="h-full flex flex-col" style={{ background: '#F5F7FA', fontFamily: '\'Noto Sans SC\', sans-serif' }}>
      {/* 顶部导航 */}
      <div className="flex items-center justify-between px-4 pt-12 pb-3 flex-shrink-0">
        <button onClick={() => navigate(-1)} className="w-8 h-8 flex items-center justify-center cursor-pointer">
          <i className="ri-arrow-left-line text-xl" style={{ color: '#A8B8D8' }} />
        </button>
        {/* 日期选择器 */}
        <div className="flex items-center gap-3">
          <button onClick={handlePrevDate} className="w-7 h-7 flex items-center justify-center cursor-pointer">
            <i className="ri-arrow-left-s-line text-lg" style={{ color: '#C5B9D8' }} />
          </button>
          <span className="font-semibold text-sm" style={{ color: '#2D2B3D' }}>{formatDate(currentDate)}</span>
          <button onClick={handleNextDate} className="w-7 h-7 flex items-center justify-center cursor-pointer">
            <i className="ri-arrow-right-s-line text-lg" style={{ color: '#C5B9D8' }} />
          </button>
        </div>
        <div className="relative">
          <button onClick={() => setShowMenu(!showMenu)} className="w-8 h-8 flex items-center justify-center cursor-pointer">
            <i className="ri-more-line text-xl" style={{ color: '#A8B8D8' }} />
          </button>
          {showMenu && (
            <div
              className="absolute right-0 top-10 rounded-2xl overflow-hidden z-50"
              style={{ background: 'rgba(255,255,255,0.96)', backdropFilter: 'blur(16px)', border: '1px solid rgba(181,198,224,0.25)', minWidth: 120 }}
            >
              <button className="w-full flex items-center gap-2 px-4 py-3 cursor-pointer" style={{ color: '#A8B8D8', fontSize: 14 }} onClick={() => setShowMenu(false)}>
                <i className="ri-share-line" />分享
              </button>
              <div className="h-px mx-3" style={{ background: 'rgba(155,142,196,0.15)' }} />
              <button className="w-full flex items-center gap-2 px-4 py-3 cursor-pointer" style={{ color: '#E57373', fontSize: 14 }} onClick={() => { setRecords(prev => prev.filter(r => r.date !== currentDate)); setShowMenu(false); }}>
                <i className="ri-delete-bin-line" />删除
              </button>
            </div>
          )}
        </div>
      </div>

      {/* 可滚动内容区 */}
      <div className="flex-1 overflow-y-auto px-4" style={{ scrollbarWidth: 'none' }}>
        {/* 照片网格 */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          {/* 添加按钮 */}
          <button
            onClick={handleAddPhoto}
            className="aspect-square rounded-2xl flex flex-col items-center justify-center gap-2 cursor-pointer active:scale-95 transition-transform"
            style={{ border: '2px dashed rgba(155,142,196,0.4)', background: 'rgba(232,226,245,0.5)' }}
          >
            <i className="ri-add-line text-3xl" style={{ color: '#C5B9D8' }} />
            <span className="text-xs" style={{ color: '#C5B9D8' }}>添加照片</span>
          </button>

          {/* 照片卡片 */}
          {photos.map((photo, i) => (
            <div key={i} className="aspect-square rounded-2xl overflow-hidden relative group cursor-pointer" onClick={() => setPreviewPhoto(photo)}>
              <img src={photo} alt="" className="w-full h-full object-cover object-top" />
              <button
                onClick={e => { e.stopPropagation(); handleDeletePhoto(photo); }}
                className="absolute bottom-2 right-2 w-7 h-7 rounded-full flex items-center justify-center cursor-pointer"
                style={{ background: 'rgba(0,0,0,0.4)' }}
              >
                <i className="ri-delete-bin-line text-white" style={{ fontSize: 13 }} />
              </button>
            </div>
          ))}
        </div>

        {/* 文字备注区 */}
        <div
          className="rounded-2xl p-4 mb-4"
          style={{ background: 'rgba(255,255,255,0.75)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.7)' }}
        >
          <textarea
            className="w-full bg-transparent outline-none resize-none text-sm"
            style={{ color: '#2D2B3D', minHeight: 100, fontFamily: '\'Noto Sans SC\', sans-serif' }}
            placeholder="写下今天的感受..."
            value={note || currentRecord?.note || ''}
            onChange={e => {
              if (e.target.value.length <= 500) setNote(e.target.value);
            }}
            maxLength={500}
          />
          <div className="flex justify-end mt-2">
            <span className="text-xs" style={{ color: '#C5B9D8' }}>{(note || currentRecord?.note || '').length}/500</span>
          </div>
        </div>

        {/* 历史记录预览 */}
        <div className="mb-4">
          <p className="text-xs font-medium mb-2" style={{ color: '#8E8E9A' }}>近期记录</p>
          <div className="flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
            {records.filter(r => r.photos.length > 0).map(r => (
              <button
                key={r.id}
                onClick={() => setCurrentDate(r.date)}
                className="flex-shrink-0 cursor-pointer"
              >
                <div
                  className="w-16 h-16 rounded-xl overflow-hidden"
                  style={{ border: r.date === currentDate ? '2px solid #A8B8D8' : '2px solid transparent' }}
                >
                  <img src={r.photos[0]} alt="" className="w-full h-full object-cover object-top" />
                </div>
                <p className="text-center mt-1" style={{ fontSize: 9, color: '#C5B9D8' }}>
                  {r.date.slice(5).replace('-', '/')}
                </p>
              </button>
            ))}
          </div>
        </div>

        <div className="pb-4" />
      </div>

      {/* 底部操作栏 */}
      <div className="flex gap-3 px-4 pb-8 pt-3 flex-shrink-0" style={{ borderTop: '1px solid rgba(155,142,196,0.12)' }}>
        <button
          onClick={() => navigate(-1)}
          className="flex-1 h-12 rounded-2xl font-medium cursor-pointer whitespace-nowrap"
          style={{ color: '#8E8E9A', background: 'rgba(155,142,196,0.1)', fontSize: 15 }}
        >
          取消
        </button>
        <button
          onClick={handleSave}
          className="flex-1 h-12 rounded-2xl font-semibold text-white cursor-pointer whitespace-nowrap active:scale-95 transition-transform"
          style={{ background: saved ? 'rgba(181,198,224,0.6)' : 'linear-gradient(135deg,#B5C6E0,#A8B8D8)', fontSize: 15, boxShadow: '0 6px 20px rgba(181,198,224,0.45)' }}
        >
          {saved ? '已保存 ✓' : '保存'}
        </button>
      </div>

      {/* 隐藏文件输入 */}
      <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFileChange} />

      {/* 照片预览弹窗 */}
      {previewPhoto && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/70" onClick={() => setPreviewPhoto(null)}>
          <img src={previewPhoto} alt="" className="max-w-full max-h-full rounded-2xl object-contain" style={{ maxWidth: '90%', maxHeight: '80%' }} />
          <button className="absolute top-12 right-4 w-10 h-10 flex items-center justify-center rounded-full cursor-pointer" style={{ background: 'rgba(255,255,255,0.2)' }}>
            <i className="ri-close-line text-white text-xl" />
          </button>
        </div>
      )}

      {/* 点击外部关闭菜单 */}
      {showMenu && <div className="absolute inset-0 z-40" onClick={() => setShowMenu(false)} />}
    </div>
  );
}
